import { type ReactElement } from 'react';
import { JobsTablePanel } from '@/features/jobs/components/JobsTablePanel';
import { useJobsPageViewModel } from '@/features/jobs/hooks/useJobsPageViewModel';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { EntityDailyHistoryPanel } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import { getDetailLabel } from '@/shared/copy/details';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';

export const JobsPage = (): ReactElement => {
  const {
    metricSectionProps,
    historyRows,
    historyDayKeys,
    historyLoading,
    historyHasError,
    historyNoRowsText,
    historyDateRangeLabel,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    historyTotalCount,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
    tablePanelProps,
  } = useJobsPageViewModel();

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Jobs
        </Text>
      }
      charts={<StackedMetricsStatusChartSection {...metricSectionProps} />}
      content={
        <Stack gap="2xl">
          <EntityDailyHistoryPanel
            title="Job History"
            dateRangeLabel={historyDateRangeLabel}
            dayKeys={historyDayKeys}
            entityLabel="Job"
            activityLabel="runs"
            rows={historyRows}
            loading={historyLoading}
            showErrorState={historyHasError}
            noRowsText={historyNoRowsText}
            defaultSortKey="pipelineLabel"
            sortKey={historySortKey}
            sortDirection={historySortDirection}
            sortOptions={[
              { key: 'pipelineLabel', label: getDetailLabel('pipeline') },
              { key: 'stageLabel', label: getDetailLabel('stage') },
              { key: 'name', label: 'Job' },
            ]}
            getSortValue={(row, sortKey) => {
              if (sortKey === 'pipelineLabel') {
                return row.prefixParts?.[0]?.label ?? '';
              }
              if (sortKey === 'stageLabel') {
                return row.prefixParts?.[1]?.label ?? '';
              }
              if (sortKey === 'prefixLabel') {
                return row.prefixLabel ?? '';
              }
              return row.name;
            }}
            onSortChange={(nextSortKey, nextSortDirection) => {
              setHistorySortKey(nextSortKey);
              setHistorySortDirection(nextSortDirection);
              setHistoryPage(0);
            }}
            page={historyPage}
            pageSize={historyPageSize}
            totalCount={historyTotalCount}
            onPageChange={setHistoryPage}
            onPageSizeChange={(nextPageSize) => {
              setHistoryPageSize(nextPageSize);
              setHistoryPage(0);
            }}
          />
          <JobsTablePanel {...tablePanelProps} />
        </Stack>
      }
    />
  );
};
